import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, orderBy, limit, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  FlaskConical, CheckCircle2, XCircle, Circle, ChevronRight,
  PlayCircle, Loader2, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { db, functions } from '../../config/firebase';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import { agruparPorArea } from '../../data/escenariosPruebas';
import type { EstadoResultado, CorridaE2E } from '../../types/pruebas';
import styles from './PruebasListaPage.module.css';

interface ResultadoResumen {
  estado: EstadoResultado;
}

type FirestoreTs = { toDate?: () => Date } | undefined;

function formatearFecha(ts: FirestoreTs): string {
  const d = ts?.toDate?.();
  return d ? d.toLocaleString('es-PA', { dateStyle: 'short', timeStyle: 'medium' }) : '';
}

export default function PruebasListaPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [resultados, setResultados] = useState<Record<string, ResultadoResumen>>({});
  const [loading, setLoading] = useState(true);
  const [corrida, setCorrida] = useState<CorridaE2E | null>(null);
  const [disparando, setDisparando] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'pruebas_resultados'),
      (snap) => {
        const map: Record<string, ResultadoResumen> = {};
        snap.forEach((d) => {
          const data = d.data();
          if (data.estado === 'pass' || data.estado === 'fail') {
            map[d.id] = { estado: data.estado };
          }
        });
        setResultados(map);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, []);

  // Suite automatizada (Playwright, vía GitHub Actions) — siempre la corrida
  // más reciente, así se ve el estado en vivo apenas se dispara y persiste el
  // resultado de la última corrida aunque se recargue la página.
  useEffect(() => {
    const q = query(collection(db, 'pruebas_e2e_runs'), orderBy('iniciadoEn', 'desc'), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      setCorrida(snap.empty ? null : (snap.docs[0].data() as CorridaE2E));
    });
    return () => unsub();
  }, []);

  const dispararPruebas = useCallback(async () => {
    setDisparando(true);
    try {
      const fn = httpsCallable(functions, 'portal_dispararPruebasE2E');
      await fn({});
      addToast('success', 'Corrida disparada en GitHub Actions — puede tardar varios minutos en aparecer el resultado.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo disparar la suite automatizada.');
    } finally {
      setDisparando(false);
    }
  }, [addToast]);

  const corriendoAhora = disparando || corrida?.estado === 'en_progreso';

  const grupos = useMemo(() => agruparPorArea(), []);
  const total = useMemo(() => grupos.reduce((n, g) => n + g.escenarios.length, 0), [grupos]);

  const conteo = useMemo(() => {
    let pass = 0, fail = 0;
    for (const r of Object.values(resultados)) {
      if (r.estado === 'pass') pass++;
      else if (r.estado === 'fail') fail++;
    }
    return { pass, fail, sinCorrer: total - pass - fail };
  }, [resultados, total]);

  return (
    <MainLayout
      pageTitle="Pruebas manuales de teleconsulta"
      pageSubtitle="Escenarios paso a paso para validar toda la funcionalidad con usuarios reales, desde la app móvil hasta el portal."
    >
      <div className={styles.page}>
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryNum}>{total}</span>
            <span className={styles.summaryLabel}>Escenarios</span>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryPass}`}>
            <span className={styles.summaryNum}>{loading ? '—' : conteo.pass}</span>
            <span className={styles.summaryLabel}>Pass</span>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryFail}`}>
            <span className={styles.summaryNum}>{loading ? '—' : conteo.fail}</span>
            <span className={styles.summaryLabel}>Fail</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryNum}>{loading ? '—' : conteo.sinCorrer}</span>
            <span className={styles.summaryLabel}>Sin correr</span>
          </div>
        </div>

        <section className={styles.e2eCard}>
          <div className={styles.e2eHeader}>
            <div>
              <h2 className={styles.areaTitle} style={{ margin: 0 }}>Pruebas automatizadas (Playwright)</h2>
              <p className={styles.e2eSubtitle}>
                Corre en GitHub Actions (no en esta pestaña) — tarda varios minutos.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={dispararPruebas}
              disabled={corriendoAhora}
            >
              {corriendoAhora ? (
                <>
                  <Loader2 size={14} className={styles.spin} aria-hidden="true" />
                  Corriendo…
                </>
              ) : (
                <>
                  <PlayCircle size={14} aria-hidden="true" />
                  Correr pruebas automatizadas
                </>
              )}
            </Button>
          </div>

          {corrida && (
            <div className={styles.e2eBody}>
              {corrida.estado === 'en_progreso' && (
                <p className={styles.e2eStatusLine}>
                  <Loader2 size={14} className={styles.spin} aria-hidden="true" />
                  En progreso — iniciada {formatearFecha(corrida.iniciadoEn as FirestoreTs)}
                  {corrida.iniciadoPorNombre ? ` por ${corrida.iniciadoPorNombre}` : ''}.
                </p>
              )}

              {corrida.estado === 'completado' && corrida.resumen && (
                <p className={styles.e2eStatusLine}>
                  {corrida.resumen.fail === 0 && !corrida.resumen.skip ? (
                    <CheckCircle2 size={14} className={styles.e2eIconOk} aria-hidden="true" />
                  ) : (
                    <XCircle size={14} className={styles.e2eIconFail} aria-hidden="true" />
                  )}
                  Última corrida ({formatearFecha(corrida.finalizadoEn as FirestoreTs)}):{' '}
                  <strong>{corrida.resumen.pass}</strong> pass · <strong>{corrida.resumen.fail}</strong> fail
                  {corrida.resumen.skip > 0 && <> · <strong>{corrida.resumen.skip}</strong> sin correr (bloqueadas por un fallo previo)</>}
                  {' '}de {corrida.resumen.total}.
                  {corrida.workflowRunUrl && (
                    <a href={corrida.workflowRunUrl} target="_blank" rel="noreferrer" className={styles.e2eLink}>
                      Ver corrida <ExternalLink size={11} aria-hidden="true" />
                    </a>
                  )}
                </p>
              )}

              {corrida.estado === 'error' && (
                <p className={styles.e2eStatusLine}>
                  <AlertTriangle size={14} className={styles.e2eIconFail} aria-hidden="true" />
                  {corrida.error || 'La corrida no pudo completarse.'}
                  {corrida.workflowRunUrl && (
                    <a href={corrida.workflowRunUrl} target="_blank" rel="noreferrer" className={styles.e2eLink}>
                      Ver corrida <ExternalLink size={11} aria-hidden="true" />
                    </a>
                  )}
                </p>
              )}

              {corrida.resultados?.some((r) => r.estado === 'fail') && (
                <ul className={styles.e2eFailList}>
                  {corrida.resultados.filter((r) => r.estado === 'fail').map((r, i) => (
                    <li key={i}>
                      <span className={styles.e2eFailArchivo}>{r.archivo}</span> — {r.nombre}
                      {r.error && <span className={styles.e2eFailError}>{r.error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        {grupos.map((grupo) => (
          <section key={grupo.area} className={styles.areaSection}>
            <h2 className={styles.areaTitle}>{grupo.area}</h2>
            <div className={styles.escenarioList}>
              {grupo.escenarios.map((esc) => {
                const r = resultados[esc.id];
                return (
                  <button
                    key={esc.id}
                    type="button"
                    className={styles.escenarioRow}
                    onClick={() => navigate(`/admin/pruebas/${esc.id}`)}
                  >
                    <span className={styles.escenarioId}>{esc.id}</span>
                    <span className={styles.escenarioTitulo}>{esc.titulo}</span>
                    <span className={styles.escenarioEstado}>
                      {!r && (
                        <Badge variant="neutral" size="sm">
                          <Circle size={11} style={{ marginRight: 4 }} aria-hidden="true" />
                          Sin correr
                        </Badge>
                      )}
                      {r?.estado === 'pass' && (
                        <Badge variant="success" size="sm">
                          <CheckCircle2 size={11} style={{ marginRight: 4 }} aria-hidden="true" />
                          Pass
                        </Badge>
                      )}
                      {r?.estado === 'fail' && (
                        <Badge variant="error" size="sm">
                          <XCircle size={11} style={{ marginRight: 4 }} aria-hidden="true" />
                          Fail
                        </Badge>
                      )}
                    </span>
                    <ChevronRight size={16} className={styles.escenarioChevron} aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        {total === 0 && (
          <div className={styles.emptyWrap}>
            <FlaskConical size={40} aria-hidden="true" />
            <p>No hay escenarios de prueba configurados.</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
